// src/utils/docxExport.js
import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";
import showdown from "showdown";

export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const isIOS = () => /iP(ad|hone|od)/.test(navigator.userAgent) && !window.MSStream;

/** iOS: must be called synchronously from the click handler */
export const openIosStagingTab = () => {
  if (!isIOS()) return null;
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(
      "<!doctype html><title>Preparing…</title>" +
        "<body style='font-family:system-ui,-apple-system,Segoe UI,Arial;padding:16px'>" +
        "<p>Preparing your file…</p>" +
        "<p>If nothing happens in a few seconds, close this tab and try again.</p>" +
        "</body>"
    );
  }
  return w;
};

const tryShareFile = async (blob, filename) => {
  try {
    const file = new File([blob], filename, { type: DOCX_MIME });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: filename, text: filename });
      return true;
    }
  } catch (e) {
    console.warn("navigator.share failed:", e);
  }
  return false;
};

const deliverDocx = async (rawBlob, filename, preOpenedWin = null) => {
  const blob =
    rawBlob.type === DOCX_MIME ? rawBlob : new Blob([rawBlob], { type: DOCX_MIME });

  if (isIOS()) {
    if (await tryShareFile(blob, filename)) {
      if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
      return;
    }
    if (preOpenedWin && !preOpenedWin.closed) {
      const url = URL.createObjectURL(blob);
      preOpenedWin.document.body.innerHTML = `
        <main style="font-family:system-ui,-apple-system,Segoe UI,Arial;padding:24px;line-height:1.5">
          <h1 style="font-size:20px;margin:0 0 12px">Your file is ready</h1>
          <p>Tap the button below, then use the <strong>Share</strong> button to <em>Save to Files</em> or open in Word/Pages.</p>
          <p><a href="${url}" style="display:inline-block;padding:12px 16px;border-radius:10px;background:#245580;color:#fff;text-decoration:none">Open file</a></p>
          <p style="color:#666;margin-top:16px">${filename}</p>
        </main>`;
      preOpenedWin.addEventListener("pagehide", () => URL.revokeObjectURL(url), {
        once: true,
      });
      return;
    }
  }

  // Desktop/Android
  saveAs(blob, filename);
  if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
};

const fetchLessonsByRefs = async (refs, apiUrl, language) => {
  const out = [];
  for (const { moduleKey, lessonKey, id } of refs) {
    const url = `${apiUrl}/module/${moduleKey}/lesson/${lessonKey}?lang=${language}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText} for ${url}`);
    const json = await res.json(); // { title, content }
    out.push({
      moduleNum: parseInt(moduleKey.replace("module", ""), 10),
      id,
      title: json.title || `${moduleKey.toUpperCase()} – ${lessonKey}`,
      content: json.content || "",
    });
  }
  out.sort((a, b) => a.moduleNum - b.moduleNum || a.id - b.id);
  return out;
};

const buildAllInOneHtml = (
  lessons,
  docTitle,
  { courseIntro = "", moduleLabel = "Module", showModuleHeadings = true, renumberSingleModuleTo1 = false } = {}
) => {
  const converter = new showdown.Converter();
  const parts = [];
  parts.push(`
    <h1>${docTitle}</h1>
    <p>${courseIntro}</p>
  `);

  let currentMod = null;
  let singleModuleCounter = 0;

  for (const l of lessons) {
    if (showModuleHeadings && currentMod !== l.moduleNum) {
      currentMod = l.moduleNum;
      parts.push(`<h2 style="color:#245580">${moduleLabel} ${currentMod}</h2>`);
    }

    const html = converter.makeHtml(l.content || "");
    const displayNum = renumberSingleModuleTo1 ? ++singleModuleCounter : l.id;

    parts.push(`
      <section>
        <h3>${displayNum}. ${l.title}</h3>
        ${html}
      </section>
      <hr/>
    `);
  }

  return `
    <!doctype html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; }
        h1 { font-size: 24pt; margin-bottom: 8px; }
        h2 { font-size: 18pt; margin-top: 20px; color: #245580; }
        h3 { font-size: 14pt; margin-top: 14px; }
        p, li { font-size: 11pt; }
        hr { border: 0; border-top: 1px solid #eee; margin: 18px 0; }
        img { max-width: 100%; }
        blockquote { padding:10px 16px; background:#f0f6ff; border-left:4px solid #0077cc; }
      </style>
    </head>
    <body>
      ${parts.join("\n")}
    </body>
    </html>
  `;
};

/**
 * High-level “export course to .docx”
 */
export const exportCourseDocx = async ({
  refs,                   // [{ moduleKey:'module0', lessonKey:'lesson1', id:1 }, ...]
  apiUrl,
  language,               // 'en' | 'pt'
  docTitle,               // string
  fileBase,               // e.g., 'personal-finance-EN'
  labels = {},            // { courseIntro, moduleLabel, showModuleHeadings, renumberSingleModuleTo1 }
  preOpenedWin = null,    // window from openIosStagingTab
}) => {
  const lessons = await fetchLessonsByRefs(refs, apiUrl, language);
  if (!lessons.length) throw new Error("No lessons found to export.");

  const html = buildAllInOneHtml(lessons, docTitle, labels);
  const blob = await asBlob(html, {
    orientation: "portrait",
    margins: { top: 720, right: 720, bottom: 720, left: 720 },
  });

  await deliverDocx(blob, `${fileBase}.docx`, preOpenedWin);
};
